/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Icon from "@insite/mobius/Icon";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@stoneage/Overrides/Components/ProductCarouselProduct";
import { ProductCardSelections } from "@stoneage/Overrides/Widgets/ProductList/ProductCardSelections";
import { color, styledSlider, xScreen } from "@stoneage/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

// eslint-disable-next-line
import { A11y, Navigation, Pagination, Scrollbar } from "swiper";
// eslint-disable-next-line
import { Swiper, SwiperSlide } from "swiper/react";

export const carouselProductsContainer = css`
    ${styledSlider()}

    .swiper-carousel {
        margin-left: 0;
    }
    .swiper-container {
        width: 100% !important;
        padding-bottom: 33px;
    }

    && .swiper-slide {
        padding: 0;
        max-width: 315px;

        ${xScreen("lt", 1024)} {
            max-width: 370px;
        }
        ${xScreen("lte", "sm")} {
            max-width: unset;
        }
    }

    && .no-swiping-elements {
        display: flex;
        flex-direction: column;
    }

    && .swiper-slide > div:first-child {
        max-width: 583px;
    }

    && .swiper-button-next,
    .swiper-button-prev {
        background-color: inherit;
        svg {
            color: ${color("common.ui7")};
            height: 19px;
        }
    }

    && .swiper-button-prev {
        left: -61px;
    }

    && .swiper-button-next {
        right: -66px;
    }

    && .swiper-navigation {
        position: absolute;
        bottom: 5px;
    }

    .swiper-pagination {
        min-width: 84px;
        display: block;
        .swiper-pagination-bullet {
            margin-right: 0 !important;
            height: 8px;
            width: 8px;
            background-color: ${color("common.ui4")};
            border: 1px solid ${color("common.ui6")};
            transform: unset;
        }
    }
    && .swiper-pagination-bullet-active {
        background: ${color("common.ui7")};
    }
    && .swiper-pagination-bullets-dynamic {
        bottom: 0px;
    }
`;

interface Props {
    id: string;
    fields: ProductCardSelections;
    products: ProductModel[];
    isProductDetailsPage?: boolean;
    isProductListPage?: boolean;
    extendedStyles?: ProductCarouselProductStyles;
    imageDimensions?: number;
    hideAvailability?: boolean;
    relatedProductsEventHandler?: (destination: string) => void;
}

const ProductCarouselComponent: FC<Props> = ({
    id,
    fields,
    products,
    extendedStyles,
    imageDimensions,
    relatedProductsEventHandler,
}) => {
    if (!products?.length) {
        return null;
    }

    const [showSpinner, setShowSpinner] = useState(false);
    const [slides, setSlides] = useState(1);

    useEffect(() => {
        window.addEventListener("resize", getDimensions);
        const getStyles = async () => {
            // eslint-disable-next-line global-require
            const result = await require("swiper/swiper-bundle.min.css");
        };
        getStyles().then(result => {
            setShowSpinner(false);
        });
        return () => {
            window.removeEventListener("resize", getDimensions);
        };
    }, []);

    const getDimensions = (swiper: any) => {
        if (swiper?.params) {
            const elementWidth = document.getElementsByClassName(`${id}`)[0]?.getBoundingClientRect().width;
            if (!elementWidth) {
                return;
            }

            if (elementWidth < 550 || window?.innerWidth < 580) {
                setSlides(1);
            } else if ((elementWidth <= 760 && elementWidth > 580) || window?.innerWidth <= 768) {
                setSlides(2);
            } else if ((elementWidth <= 1000 && elementWidth > 760) || window?.innerWidth <= 920) {
                setSlides(3);
            } else {
                setSlides(4);
            }
        }
    };

    const assignSwiper = (swiper: any) => {
        getDimensions(swiper);
    };

    if (showSpinner) {
        return (
            <StyledWrapper
                css={css`
                    height: 100px;
                    width: 100%;
                    padding: 10px 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                `}
            >
                <LoadingSpinner color="primary.main" />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper
            css={css`
                > div {
                    display: flex;
                    justify-content: center;
                }
                @media print {
                    display: none;
                }
            `}
        >
            <StyledWrapper className={`carousel-products-container ${id}`} css={carouselProductsContainer}>
                <Swiper
                    onInit={swiper => {
                        assignSwiper(swiper);
                    }}
                    spaceBetween={16}
                    navigation={{
                        nextEl: `.swiper-button-next.swiper-button-next-${id}`,
                        prevEl: `.swiper-button-prev.swiper-button-prev-${id}`,
                    }}
                    modules={[Navigation, Pagination, Scrollbar, A11y]}
                    initialSlide={1}
                    onResize={swiper => assignSwiper(swiper)}
                    slidesPerView={Math.min(slides, products?.length)}
                    loop={false}
                    className="swiper-carousel"
                    wrapperClass="swiper-container"
                    pagination={{
                        clickable: true,
                        dynamicBullets: true,
                        dynamicMainBullets: 3,
                        el: ".swiper-pagination",
                    }}
                    noSwipingClass="no-swiping-elements"
                >
                    {products.map((product: ProductModel) => (
                        <SwiperSlide key={`carouselProduct${product.id}`}>
                            <ProductCarouselProduct
                                carouselId={id}
                                product={product}
                                showImage={fields.showImage}
                                showPartNumbers={fields.showPartNumbers}
                                showPrice={fields.showPrice}
                                showAddToCart={fields.showAddToCart}
                                showAddToList={false}
                                showCompare={false}
                                showAttributes={true}
                                showAvailability={fields.showAvailability}
                                showTitle={fields.showTitle}
                                extendedStyles={extendedStyles}
                                imageDimensions={imageDimensions}
                                relatedProductsEventHandler={relatedProductsEventHandler}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
                <StyledWrapper className="swiper-navigation">
                    <StyledWrapper
                        className={`swiper-button-prev swiper-button-prev-${id}`}
                        aria-label="carousel-move-left-button"
                    >
                        <Icon src="ChevronLeft" size={30} color="primary" />
                    </StyledWrapper>
                    <StyledWrapper
                        className={`swiper-button-next swiper-button-next-${id}`}
                        aria-label="carousel-move-right-button"
                    >
                        <Icon src="ChevronRight" size={30} color="primary" />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default ProductCarouselComponent;
